import type { Observable } from 'rxjs';
import type { SiteContentEntity } from '@common/types/site-content.type';
import type { ShareBlocks } from '@common/types/site.type';
import type { UnmigratedBlock } from '@web/store/page-structure/page-structure/unmigrated-blocks';

import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import * as ContentSelectors from './content.selectors';
import ContentActions from './content.actions';

@Injectable()
export class ContentFacade {
    private readonly store = inject(Store);

    public readonly siteContent$: Observable<SiteContentEntity> = this.store.select(ContentSelectors.selectSiteContent);
    public readonly pageContent$: Observable<SiteContentEntity> = this.store.select(ContentSelectors.selectPageContent);
    public readonly sharedBlocks$: Observable<ShareBlocks> = this.store.select(ContentSelectors.selectSiteSharedBlocks);
    public readonly pageContentStructureUpdatedAt$: Observable<string | null> = this.store.select(
        ContentSelectors.selectPageContentStructureUpdatedAt
    );
    public readonly isDirty$: Observable<boolean> = this.store.select(ContentSelectors.selectIsDirty);
    public readonly isLoading$: Observable<boolean> = this.store.select(ContentSelectors.selectIsLoading);
    public readonly isReset$: Observable<boolean> = this.store.select(ContentSelectors.selectIsReset);
    public readonly dirtyPageId$: Observable<string | null> = this.store.select(ContentSelectors.selectDirtyPageId);
    public readonly isAutoSavePaused$: Observable<boolean> = this.store.select(ContentSelectors.selectIsAutoSavePaused);
    public readonly pageVersions$: Observable<Record<string, string>> = this.store.select(
        ContentSelectors.selectPageVersions
    );
    public readonly staleVersionPageIds$: Observable<string[]> = this.store.select(
        ContentSelectors.selectStaleVersionPageIds
    );
    public readonly deletedPageIds$: Observable<string[]> = this.store.select(ContentSelectors.selectDeletedPageIds);
    public readonly unmigratedBlocks$: Observable<UnmigratedBlock[]> = this.store.select(
        ContentSelectors.selectUnmigratedBlocks
    );

    public setSharedBlocks(blocks: ShareBlocks): void {
        this.store.dispatch(ContentActions.setSharedBlocks({ blocks }));
    }

    public updateSiteThemeSettingsContent(themeSettings: string): void {
        this.store.dispatch(ContentActions.updateSiteThemeSettingsContent({ themeSettings }));
    }

    public setIsDirty(isDirty: boolean): void {
        this.store.dispatch(ContentActions.setIsDirty({ isDirty }));
    }

    public setIsLoading(isLoading: boolean): void {
        this.store.dispatch(ContentActions.setIsLoading({ isLoading }));
    }

    public triggerSaveContent(): void {
        this.store.dispatch(ContentActions.triggerSaveContent());
    }

    public setIsReset(isReset: boolean): void {
        this.store.dispatch(ContentActions.setIsReset({ isReset }));
    }

    public setDirtyPageId(pageId: string | number): void {
        this.store.dispatch(ContentActions.setDirtyPageId({ pageId }));
    }

    public setUnmigratedBlocks(blocks: UnmigratedBlock[]): void {
        this.store.dispatch(ContentActions.setUnmigratedBlocks({ blocks }));
    }

    public setAutoSavePaused(isPaused: boolean): void {
        this.store.dispatch(ContentActions.setAutoSavePaused({ isPaused }));
    }
}
